<script lang="ts" setup>
import { watch, ref, onMounted } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { UserAccountDocument, UserType } from '@/gql/graphql';
import { BAlert, BButton, BSpinner, BCollapse } from 'bootstrap-vue-next';
import UserMfa from '@/components/user/UserMfa.vue';
import UserPersonalDetails from '@/components/user/UserPersonalDetails.vue';
import UserListOfApiKeys from '@/components/user/UserListOfApiKeys.vue';
import UserNewApiKeyForm from '@/components/user/UserNewApiKeyForm.vue';
import UserNotifications from '@/components/user/UserNotifications.vue';
import { NotificationItem } from '@/utils/notification-utils';
import { useUserStore } from '@/store/user';
import { deepParseDates } from '@/utils/date-utils';

const userStore = useUserStore();

// --- Local State ---
const userDetails = ref<UserType>();
const newApiKeyVisible = ref(false);

// --- Query User Details ---
const {
  result: resultDetails,
  loading: loadingDetails,
  error: errorFetchDetails,
  refetch: refetchDetails,
} = useQuery(UserAccountDocument);

function setUserDetails() {
  if (resultDetails.value?.user) {
    const user = resultDetails.value.user;
    let notifications: NotificationItem[] = [];

    // If notifications are stored as a JSON string, parse them; otherwise, assume they are already an array.
    if (user.notifications) {
      try {
        notifications =
          typeof user.notifications === 'string'
            ? JSON.parse(user.notifications)
            : user.notifications;
      } catch (error) {
        console.error('Failed to parse notifications:', error);
        notifications = [];
      }
    }

    // Replace the entire object to trigger eventual watcher in child components
    userDetails.value = deepParseDates({
      ...user,
      notifications: notifications,
    }) as UserType;
  }
}

// Handle the result of the currentUser query
watch(resultDetails, (value) => {
  console.log(value);
  if (value?.user) {
    console.debug('User details retrieved');
    setUserDetails();
  } else {
    console.error('Error, could not retrieve user details.');
  }
});

function requestRefetchDetails() {
  refetchDetails()
    ?.catch((err) => {
      console.error('Refetch failed:', err);
    })
    ?.then(() => {
      console.debug('refetched');
      setUserDetails();
    });
}

// --- handle emits from child components ---
function handleUserUpdated() {
  requestRefetchDetails();
}
function handleApiKeyRevoked() {
  requestRefetchDetails();
}
function handleApiKeyCreated() {
  requestRefetchDetails();
}

// --- RE-FETCH DATA WHEN VIEW ACTIVATED ---
onMounted(() => {
  if (!loadingDetails.value && !errorFetchDetails.value) {
    setUserDetails();
  }
});
</script>

<template>
  <div>
    <h1><font-awesome-icon :icon="['fas', 'user']" /> Account Information</h1>
    <h5>Manager your personal details and account settings</h5>
    <hr />
    <p>
      Manage your essential account information. Update your name to keep your
      profile current, securely change your password to protect your account,
      and customize your notification settings to control how you receive alerts
      and updates from our platform.
    </p>

    <!-- Query Error -->
    <b-alert
      v-model="errorFetchDetails"
      variant="danger"
      class="mt-3"
      dismissible
    >
      {{ errorFetchDetails }}
      <b-button
        @click="requestRefetchDetails"
        variant="outline-dark"
        class="btn-sm"
      >
        <font-awesome-icon :icon="['fas', 'sync']" />
        Retry</b-button
      >
    </b-alert>

    <!-- Loading Spinner -->
    <div v-if="loadingDetails" class="mt-3">
      <b-spinner small label="Loading..." />
    </div>

    <!-- PERSONAL DETAILS FORM -->
    <h4>
      <font-awesome-icon :icon="['fas', 'user']" />
      Account Details
    </h4>

    <UserPersonalDetails
      v-if="userDetails"
      :user="userDetails"
      :loading="loadingDetails"
      :error="errorFetchDetails"
      @userUpdated="handleUserUpdated"
    />

    <!-- MULTI-FACTOR AUTHENTICATION (MFA) -->
    <h4 class="mt-5 mb-3">
      <font-awesome-icon :icon="['fas', 'shield-alt']" />
      Multi-Factor Authentication (MFA)
    </h4>

    <UserMfa v-if="userDetails" />

    <!-- NOTIFICATIONS -->
    <h4 class="mt-5 mb-3">
      <font-awesome-icon :icon="['fas', 'bell']" />
      Notification Settings
    </h4>
    <UserNotifications
      v-if="userDetails && userDetails.notifications"
      :notifications="userDetails.notifications"
      :loading="loadingDetails"
      @userUpdated="handleUserUpdated"
    />

    <!-- API only for developers -->
    <div v-if="userStore.developer">
      <h4 class="mt-5 mb-3">
        <font-awesome-icon :icon="['fas', 'shield-halved']" />
        Personal Access Tokens
      </h4>

      <b-alert v-model="userStore.developer" variant="warning" class="mt-3">
        <strong>Warning:</strong> This section is only for developers. Please
        proceed with caution.
      </b-alert>

      <!-- API KEYS LIST + Revoke -->
      <UserListOfApiKeys
        v-if="userDetails && userDetails.apiKeys"
        :apiKeys="userDetails.apiKeys ?? []"
        :loading="loadingDetails"
        :error="errorFetchDetails"
        @apiKeyRevoked="handleApiKeyRevoked"
      />

      <!-- NEW ACCESS TOKEN FORM -->
      <h4 v-if="newApiKeyVisible" class="mt-4">
        <font-awesome-icon :icon="['fas', 'circle-plus']" />
        Create new access token
      </h4>
      <b-collapse id="collapse-new-api-key" v-model="newApiKeyVisible">
        <UserNewApiKeyForm
          @apiKeyCreated="handleApiKeyCreated"
          @api-key-dismissed="newApiKeyVisible = false"
        />
      </b-collapse>
      <b-button
        @click="newApiKeyVisible = !newApiKeyVisible"
        aria-controls="collapse-new-api-key"
        variant="outline-dark"
        class="my-2"
      >
        <span v-if="!newApiKeyVisible">Create new access token</span>
        <span v-else>Cancel</span>
      </b-button>
    </div>
  </div>
</template>
<style scoped></style>
